import { notifications } from '@mantine/notifications';
import { ConnectionState, type Room, RoomEvent } from 'livekit-client';

import { LOCAL_RECORDING_ATTRIBUTE } from './attribute';
import { createMeetingCompositor, type MeetingCompositor } from './compositor';
import { reportLocalRecordingEvent } from './metrics';
import { type LocalRecordingInterruption, notifySaved } from './notifications';
import {
    downloadRecording,
    holdRecordingLock,
    markRecordingSaved,
    openRecordingSink,
    type RecordingMeta,
    type RecordingSink,
    recordingFileName,
} from './storage';
import { currentBrowserEnvironment, detectLocalRecordingSupport, type LocalRecordingSupport } from './support';
import { i18n } from '@/i18n';
import { notifyError } from '@/shared/notify';

const TIMESLICE_MS = 1000;
const VIDEO_BITS_PER_SECOND = 2_500_000;
const AUDIO_BITS_PER_SECOND = 128_000;

export type LocalRecordingStatus = 'idle' | 'starting' | 'recording' | 'saving';

export type LocalRecordingErrorKind = 'start' | 'storage' | 'recorder' | 'save';

export type LocalRecordingState = {
    status: LocalRecordingStatus;
    startedAt?: number;
    storage?: RecordingSink['kind'];
    error?: { kind: LocalRecordingErrorKind; error: Error };
    savedFile?: { name: string; size: number };
};

type Session = {
    meta: RecordingMeta;
    compositor: MeetingCompositor;
    sink: RecordingSink;
    recorder: MediaRecorder;
    releaseLock: () => void;
    detach: () => void;
};

const asError = (error: unknown) => (error instanceof Error ? error : new Error(String(error)));

export class LocalRecordingController {
    readonly support: LocalRecordingSupport = detectLocalRecordingSupport(currentBrowserEnvironment());
    private state: LocalRecordingState = { status: 'idle' };
    private readonly listeners = new Set<() => void>();
    private session: Session | undefined;
    private stopping: Promise<void> | undefined;
    private interruption: LocalRecordingInterruption | undefined;

    private readonly room: Room;

    constructor(room: Room) {
        this.room = room;
    }

    readonly subscribe = (listener: () => void) => {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    };

    readonly getState = () => this.state;

    private setState(patch: Partial<LocalRecordingState>) {
        this.state = { ...this.state, ...patch };
        for (const listener of this.listeners) listener();
    }

    private fail(kind: LocalRecordingErrorKind, error: unknown, patch: Partial<LocalRecordingState> = {}) {
        const repeated = this.state.error?.kind === kind;
        this.setState({ ...patch, error: { kind, error: asError(error) } });
        if (!repeated) reportLocalRecordingEvent('failed');
        if (kind === 'storage' || kind === 'recorder') {
            console.error(error);
            this.interruption ??= kind;
            void this.stop();
            return;
        }
        if (!repeated) notifyError(i18n.t(`conference:localRecording.errors.${kind}`), error);
    }

    readonly start = async () => {
        if (this.state.status !== 'idle' || !this.support.supported) return;
        const { mimeType } = this.support;
        const meta: RecordingMeta = { id: crypto.randomUUID(), roomName: this.room.name, mimeType, startedAt: Date.now() };
        this.interruption = undefined;
        this.setState({ status: 'starting', error: undefined, savedFile: undefined, startedAt: undefined });
        let compositor: MeetingCompositor | undefined;
        try {
            compositor = createMeetingCompositor(this.room);
            const sink = await openRecordingSink(meta, (error) => {
                this.fail('storage', error);
            });
            if (sink.kind === 'memory') {
                notifications.show({ color: 'warning', title: i18n.t('conference:localRecording.memoryStorage'), message: null });
            }
            const recorder = new MediaRecorder(compositor.stream, {
                mimeType,
                videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
                audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
            });
            recorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0) sink.write(event.data);
            });
            recorder.addEventListener('error', (event) => {
                this.fail('recorder', (event as Event & { error?: unknown }).error ?? 'MediaRecorder error');
            });
            recorder.start(TIMESLICE_MS);
            this.session = {
                meta,
                compositor,
                sink,
                recorder,
                releaseLock: holdRecordingLock(meta.id),
                detach: this.attachPageGuards(),
            };
            this.setState({ status: 'recording', startedAt: meta.startedAt, storage: sink.kind });
            reportLocalRecordingEvent('started');
            this.room.localParticipant
                .setAttributes({ [LOCAL_RECORDING_ATTRIBUTE]: String(meta.startedAt) })
                .catch((error: unknown) => {
                    console.warn('Local recording attribute was not published', error);
                });
        } catch (error) {
            compositor?.stop();
            this.fail('start', error, { status: 'idle' });
        }
    };

    readonly stop = () => {
        this.stopping ??= this.finish().finally(() => {
            this.stopping = undefined;
        });
        return this.stopping;
    };

    private async finish() {
        const session = this.session;
        if (!session) return;
        this.session = undefined;
        this.setState({ status: 'saving' });
        session.detach();
        if (this.room.state === ConnectionState.Connected) {
            this.room.localParticipant.setAttributes({ [LOCAL_RECORDING_ATTRIBUTE]: '' }).catch(() => undefined);
        }
        try {
            await stopRecorder(session.recorder);
            session.compositor.stop();
            const blob = await session.sink.finish();
            const name = recordingFileName(session.meta);
            downloadRecording(blob, name);
            if (session.sink.kind === 'opfs') markRecordingSaved(session.meta);
            this.setState({ status: 'idle', startedAt: undefined, savedFile: { name, size: blob.size } });
            reportLocalRecordingEvent('saved');
            notifySaved({
                blob,
                name,
                interruption: this.interruption,
                seconds: Math.max(0, Math.floor((Date.now() - session.meta.startedAt) / 1000)),
            });
        } catch (error) {
            session.compositor.stop();
            this.fail('save', error, { status: 'idle', startedAt: undefined });
        } finally {
            session.releaseLock();
        }
    }

    private attachPageGuards() {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
        };
        const handlePageHide = () => {
            const recorder = this.session?.recorder;
            if (recorder?.state === 'recording') recorder.stop();
        };
        const handleDisconnected = () => {
            void this.stop();
        };
        globalThis.addEventListener('beforeunload', handleBeforeUnload);
        globalThis.addEventListener('pagehide', handlePageHide);
        this.room.on(RoomEvent.Disconnected, handleDisconnected);
        return () => {
            globalThis.removeEventListener('beforeunload', handleBeforeUnload);
            globalThis.removeEventListener('pagehide', handlePageHide);
            this.room.off(RoomEvent.Disconnected, handleDisconnected);
        };
    }
}

function stopRecorder(recorder: MediaRecorder): Promise<void> {
    if (recorder.state === 'inactive') return Promise.resolve();
    return new Promise((resolve) => {
        recorder.addEventListener(
            'stop',
            () => {
                resolve();
            },
            { once: true }
        );
        recorder.stop();
    });
}

const controllers = new WeakMap<Room, LocalRecordingController>();

export function localRecordingController(room: Room): LocalRecordingController {
    let controller = controllers.get(room);
    if (!controller) {
        controller = new LocalRecordingController(room);
        controllers.set(room, controller);
    }
    return controller;
}
